(function () {
  'use strict';

  var CART_EVENTS = [
    'on:cart:add',
    'on:line-item:change',
    'cart:updated',
    'cart:change',
    'shopify:cart:updated'
  ];

  function toNumber(value) {
    var number = Number(value);
    return Number.isFinite(number) ? number : 0;
  }

  function toQuantity(value) {
    return Math.max(0, Math.floor(toNumber(value)));
  }

  function withJsonSuffix(url) {
    var raw = String(url || '');
    if (!raw) return '';
    if (/\.js(?:\?|$)/.test(raw)) return raw;
    var parts = raw.split('?');
    return parts[0].replace(/\/$/, '') + '.js' + (parts[1] ? '?' + parts[1] : '');
  }

  function unique(values) {
    return Array.from(new Set(values.filter(Boolean)));
  }

  function tokens(value) {
    return String(value || '').split(/\s+/).filter(Boolean);
  }

  function normalizeSearch(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function pluralizeItems(count, singular, plural) {
    return count === 1 ? (singular || 'item') : (plural || 'items');
  }

  function replaceTokens(template, values) {
    return String(template || '').replace(/\[([a-z_]+)\]/gi, function (match, key) {
      return values[key] !== undefined ? values[key] : match;
    });
  }

  function replaceTokensWithFallback(template, fallback, values) {
    var message = replaceTokens(template, values);
    if (/\[[^\]]+\]/.test(message) && fallback && fallback !== template) {
      message = replaceTokens(fallback, values);
    }
    return message;
  }

  class BulkPurchase extends HTMLElement {
    constructor() {
      super();
      this.selected = new Map();
      this.filters = {
        material: new Set(),
        color: new Set(),
        keyword: new Set()
      };
      this.cartCount = 0;
      this.loading = false;
      this.expanded = false;
      this._cartSyncTimer = null;
      this._cartDrawerTimer = null;
      this.cartVariantQuantities = new Map();
      this.inventoryNotices = new Map();
      this.inventoryNoticeTimers = new Map();
    }

    connectedCallback() {
      this.cartCount = toQuantity(this.dataset.cartCount);
      this.eligibleProductIds = new Set(
        String(this.dataset.eligibleProductIds || '').split(',').filter(Boolean)
      );
      this.tiers = [1, 2, 3, 4].map((index) => {
        return {
          enabled: this.getAttribute('data-tier-' + index + '-enabled') === 'true',
          count: toQuantity(this.getAttribute('data-tier-' + index + '-count')),
          percent: toNumber(this.getAttribute('data-tier-' + index + '-percent'))
        };
      }).filter((tier) => tier.enabled && tier.count > 0 && tier.percent > 0)
        .sort((a, b) => a.count - b.count);
      this.maxTierCount = this.tiers.length ? this.tiers[this.tiers.length - 1].count : 0;

      this.resultsEl = this.querySelector('[data-bulk-results]');
      this.filtersEl = this.querySelector('[data-bulk-filters]');
      this.resultRows = Array.from(
        this.querySelectorAll('.bulk-purchase__results [data-bulk-variant-row]')
      );
      this.productGroups = Array.from(this.querySelectorAll('[data-bulk-product-group]'));
      this.filterButtons = Array.from(this.querySelectorAll('[data-bulk-filter-button]'));
      this.searchInputEl = this.querySelector('[data-bulk-search]');
      this.clearFiltersBtn = this.querySelector('[data-bulk-clear-filters]');
      this.visibleCountEl = this.querySelector('[data-bulk-visible-count]');
      this.noResultsEl = this.querySelector('[data-bulk-no-results]');
      this.summaryCountEl = this.querySelector('[data-bulk-summary-count]');
      this.selectedCountEl = this.querySelector('[data-bulk-selected-count]');
      this.selectedItemsEl = this.querySelector('[data-bulk-selected-items]');
      this.bottomBarEl = this.querySelector('[data-bulk-bottom-bar]');
      this.backToTopEl = this.querySelector('[data-bulk-back-to-top]');
      this.sheetPanelEl = this.querySelector('[data-bulk-selected-panel]');
      this.sheetBackdropEl = this.querySelector('[data-bulk-sheet-backdrop]');
      this.progressEl = this.querySelector('[data-bulk-progress]');
      this.progressMessageEl = this.querySelector('[data-bulk-progress-message]');
      this.progressCartEl = this.querySelector('[data-bulk-progress-cart]');
      this.progressPendingEl = this.querySelector('[data-bulk-progress-pending]');
      this.cartCountEl = this.querySelector('[data-bulk-cart-count]');
      this.pendingCountEl = this.querySelector('[data-bulk-pending-count]');
      this.addButtonEl = this.querySelector('[data-bulk-add-to-cart]');
      this.addButtonTextEl = this.querySelector('[data-bulk-add-button-text]');
      this.errorEl = this.querySelector('[data-bulk-error]');
      this.milestones = Array.from(this.querySelectorAll('[data-bulk-milestone]'));
      this.originalRowsByVariant = new Map();
      this.searchTerms = [];

      this.resultRows.forEach((row) => {
        this.originalRowsByVariant.set(String(row.dataset.variantId), row);
        row.bulkSearchText = normalizeSearch(row.dataset.search);
        row.bulkSearchCompact = row.bulkSearchText.replace(/\s+/g, '');
      });

      this._onClick = this.handleClick.bind(this);
      this._onInput = this.handleInput.bind(this);
      this._onFocusIn = this.handleFocusIn.bind(this);
      this._onSearchInput = this.handleSearchInput.bind(this);
      this._onCartEvent = this.scheduleCartSync.bind(this);
      this._onResize = this.measureFilters.bind(this);
      this._onScroll = this.updateBackToTop.bind(this);

      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onInput);
      this.addEventListener('focusin', this._onFocusIn);
      if (this.searchInputEl) this.searchInputEl.addEventListener('input', this._onSearchInput);
      CART_EVENTS.forEach((name) => document.addEventListener(name, this._onCartEvent));
      window.addEventListener('resize', this._onResize);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      if (window.ResizeObserver && this.filtersEl) {
        this._filtersObserver = new ResizeObserver(this._onResize);
        this._filtersObserver.observe(this.filtersEl);
        if (this.bottomBarEl) this._filtersObserver.observe(this.bottomBarEl);
      }

      this.measureFilters();
      this.updateBackToTop();
      this.applyFilters();
      this.updateState();
      this.syncCartCount();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('change', this._onInput);
      this.removeEventListener('focusin', this._onFocusIn);
      if (this.searchInputEl) this.searchInputEl.removeEventListener('input', this._onSearchInput);
      CART_EVENTS.forEach((name) => document.removeEventListener(name, this._onCartEvent));
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('scroll', this._onScroll);
      if (this._filtersObserver) this._filtersObserver.disconnect();
      if (this._cartSyncTimer) clearTimeout(this._cartSyncTimer);
      if (this._cartDrawerTimer) clearTimeout(this._cartDrawerTimer);
      this.inventoryNoticeTimers.forEach((timer) => clearTimeout(timer));
    }

    measureFilters() {
      if (this.filtersEl) {
        this.style.setProperty('--bulk-filters-height', this.filtersEl.offsetHeight + 'px');
      }
      if (this.bottomBarEl) {
        this.style.setProperty('--bulk-bottom-bar-height', this.bottomBarEl.offsetHeight + 'px');
      }
      this.updateBackToTop();
    }

    updateBackToTop() {
      if (!this.backToTopEl || !this.filtersEl) return;
      var filtersPassed = this.filtersEl.getBoundingClientRect().bottom < 0;
      this.backToTopEl.classList.toggle('is-visible', filtersPassed && !this.expanded);
    }

    handleClick(event) {
      var filterButton = event.target.closest('[data-bulk-filter-button]');
      if (filterButton && this.contains(filterButton)) {
        event.preventDefault();
        this.toggleFilter(filterButton);
        return;
      }

      if (event.target.closest('[data-bulk-clear-filters]')) {
        event.preventDefault();
        this.clearFilters();
        return;
      }

      if (event.target.closest('[data-bulk-cart-open]')) {
        event.preventDefault();
        this.openCartDrawer(event.target.closest('[data-bulk-cart-open]'));
        return;
      }

      if (event.target.closest('[data-bulk-back-to-top]')) {
        event.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      if (event.target.closest('[data-bulk-add-to-cart]')) {
        event.preventDefault();
        this.addToCart();
        return;
      }

      var removeSelected = event.target.closest('[data-bulk-remove-selected]');
      if (removeSelected && this.contains(removeSelected)) {
        event.preventDefault();
        this.setVariantQuantity(removeSelected.dataset.variantId, 0);
        return;
      }

      if (event.target.closest('[data-bulk-toggle-expanded]')) {
        event.preventDefault();
        this.toggleSelectedPanel();
        return;
      }

      if (event.target.closest('[data-bulk-toggle-product]')) {
        event.preventDefault();
        this.toggleProductGroup(event.target.closest('[data-bulk-toggle-product]'));
        return;
      }

      if (event.target === this.sheetBackdropEl) {
        this.closeSelectedPanel();
        return;
      }

      var plus = event.target.closest('[data-bulk-qty-plus]');
      var minus = event.target.closest('[data-bulk-qty-minus]');
      if (!plus && !minus) return;

      var row = event.target.closest('[data-bulk-variant-row]');
      if (!row) return;

      event.preventDefault();
      var input = row.querySelector('[data-bulk-qty-input]');
      var current = input ? toQuantity(input.value) : this.getVariantQuantity(row.dataset.variantId);
      this.setVariantQuantity(row.dataset.variantId, plus ? current + 1 : current - 1);
    }

    handleInput(event) {
      if (!event.target.matches('[data-bulk-qty-input]')) return;
      var row = event.target.closest('[data-bulk-variant-row]');
      if (!row) return;
      this.setVariantQuantity(row.dataset.variantId, event.target.value);
    }

    handleFocusIn(event) {
      if (!event.target.matches('[data-bulk-qty-input]')) return;
      window.requestAnimationFrame(() => {
        if (document.activeElement === event.target) event.target.select();
      });
    }

    handleSearchInput() {
      this.searchTerms = tokens(normalizeSearch(this.searchInputEl ? this.searchInputEl.value : ''));
      this.applyFilters();
    }

    toggleFilter(button) {
      var type = button.dataset.filterType;
      var value = button.dataset.filterValue;
      if (!this.filters[type] || !value) return;

      if (type !== 'color') {
        var wasActive = this.filters[type].has(value);
        this.filters[type].clear();
        if (!wasActive) this.filters[type].add(value);

        this.filterButtons.forEach((filterButton) => {
          if (filterButton.dataset.filterType !== type) return;
          var isActive = !wasActive && filterButton === button;
          filterButton.classList.toggle('is-active', isActive);
          filterButton.setAttribute('aria-pressed', String(isActive));
        });
      } else if (this.filters[type].has(value)) {
        this.filters[type].delete(value);
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      } else {
        this.filters[type].add(value);
        button.classList.add('is-active');
        button.setAttribute('aria-pressed', 'true');
      }

      this.applyFilters();
    }

    toggleProductGroup(button) {
      var group = button.closest('[data-bulk-product-group]');
      if (!group) return;
      var rows = group.querySelector('[data-bulk-product-rows]');
      var collapsed = !group.classList.contains('is-collapsed');
      group.classList.toggle('is-collapsed', collapsed);
      button.setAttribute('aria-expanded', String(!collapsed));
      if (rows) rows.hidden = collapsed;
    }

    openCartDrawer(opener) {
      this.closeSelectedPanel();
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
        bubbles: true,
        detail: { opener: opener }
      }));
    }

    clearFilters() {
      Object.keys(this.filters).forEach((key) => this.filters[key].clear());
      this.searchTerms = [];
      if (this.searchInputEl) this.searchInputEl.value = '';
      this.filterButtons.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });
      this.applyFilters();
    }

    applyFilters() {
      var visibleCount = 0;

      this.resultRows.forEach((row) => {
        var visible = this.rowMatchesFilters(row);
        row.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      this.productGroups.forEach((group) => {
        var hasVisibleRows = Boolean(group.querySelector('[data-bulk-variant-row]:not([hidden])'));
        group.hidden = !hasVisibleRows;
      });

      if (this.visibleCountEl) this.visibleCountEl.textContent = String(visibleCount);
      if (this.noResultsEl) this.noResultsEl.hidden = visibleCount > 0;
      if (this.clearFiltersBtn) {
        var hasActiveFilters = Object.keys(this.filters).some((key) => this.filters[key].size);
        this.clearFiltersBtn.hidden = !hasActiveFilters && this.searchTerms.length === 0;
      }
    }

    rowMatchesFilters(row) {
      return this.matchesSearch(row) &&
        this.matchesTokenSet(this.filters.material, row.dataset.materials) &&
        this.matchesTokenSet(this.filters.color, row.dataset.color) &&
        this.matchesKeywordFilters(row);
    }

    matchesSearch(row) {
      if (!this.searchTerms.length) return true;
      var searchText = row.bulkSearchText || '';
      var compactText = row.bulkSearchCompact || '';
      return this.searchTerms.every((term) => {
        return searchText.includes(term) || compactText.includes(term);
      });
    }

    matchesKeywordFilters(row) {
      if (!this.filters.keyword.size) return true;
      return Array.from(this.filters.keyword).every((keyword) => {
        var keywordTerms = tokens(normalizeSearch(keyword));
        return keywordTerms.length > 0 && keywordTerms.every((term) => {
          return row.bulkSearchText.includes(term) || row.bulkSearchCompact.includes(term);
        });
      });
    }

    matchesTokenSet(activeSet, rowValue) {
      if (!activeSet || activeSet.size === 0) return true;
      var rowTokens = tokens(rowValue);
      return Array.from(activeSet).every((token) => rowTokens.includes(token));
    }

    getVariantQuantity(variantId) {
      return this.selected.get(String(variantId)) || 0;
    }

    setVariantQuantity(variantId, value) {
      var id = String(variantId);
      var quantity = toQuantity(value);
      var notice = this.inventoryNotices.get(id);

      if (quantity > 0) {
        this.selected.set(id, quantity);
      } else {
        this.selected.delete(id);
      }

      if (notice && notice.persistent) {
        var hasAvailableQuantity = Number.isFinite(notice.availableQuantity);
        if (
          quantity === 0 ||
          (hasAvailableQuantity && quantity <= notice.availableQuantity) ||
          (!hasAvailableQuantity && quantity < notice.failedQuantity)
        ) {
          this.clearPersistentInventoryNotice(id);
        }
      }

      this.syncVariantInputs(id, quantity);
      this.renderSelectedItems();
      this.syncVariantInputs(id, quantity);
      this.updateState();
    }

    syncVariantInputs(variantId, quantity) {
      this.querySelectorAll('[data-variant-id="' + variantId + '"]').forEach((row) => {
        row.classList.toggle('is-selected', quantity > 0);
        var input = row.querySelector('[data-bulk-qty-input]');
        if (input) input.removeAttribute('max');
        if (input && input.value !== String(quantity)) input.value = String(quantity);
      });
      this.renderInventoryNotice(variantId);
    }

    clearSelection() {
      var ids = Array.from(this.selected.keys());
      this.selected.clear();
      ids.forEach((id) => this.syncVariantInputs(id, 0));
      this.renderSelectedItems();
      this.updateState();
    }

    renderSelectedItems() {
      if (!this.selectedItemsEl) return;
      this.selectedItemsEl.innerHTML = '';
      var selectedGroups = [];
      var groupsByProduct = Object.create(null);

      this.resultRows.forEach((row) => {
        var id = String(row.dataset.variantId);
        var quantity = this.selected.get(id);
        if (!quantity) return;

        var productKey = row.dataset.productId || row.dataset.productTitle || id;
        var group = groupsByProduct[productKey];
        if (!group) {
          group = {
            title: row.dataset.productTitle || '',
            url: row.dataset.productLink || row.dataset.productUrl || '#',
            items: []
          };
          groupsByProduct[productKey] = group;
          selectedGroups.push(group);
        }

        group.items.push({ row: row, quantity: quantity });
      });

      selectedGroups.forEach((group) => {
        if (group.title) {
          var title = document.createElement('a');
          title.className = 'bulk-purchase__selected-product-title';
          title.href = group.url;
          title.textContent = group.title;
          this.selectedItemsEl.appendChild(title);
        }

        group.items.forEach((item) => {
          var row = item.row;
          var quantity = item.quantity;

          var clone = row.cloneNode(true);
          clone.hidden = false;
          clone.removeAttribute('hidden');
          clone.classList.add('bulk-purchase__variant-row--selected');
          var input = clone.querySelector('[data-bulk-qty-input]');
          if (input) input.value = String(quantity);

          var removeButton = document.createElement('button');
          var removeItem = [group.title, row.dataset.optionSummary].filter(Boolean).join(' - ');
          var removeLabel = (this.dataset.removeTemplate || 'Remove [item]').replace(/\[item\]/g, removeItem);
          removeButton.className = 'bulk-purchase__selected-remove';
          removeButton.type = 'button';
          removeButton.dataset.bulkRemoveSelected = '';
          removeButton.dataset.variantId = String(row.dataset.variantId);
          removeButton.setAttribute('aria-label', removeLabel);
          removeButton.innerHTML = '<svg width="20" height="20" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"/><path d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5zm2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5z" fill="currentColor"/></svg>';
          clone.appendChild(removeButton);

          this.selectedItemsEl.appendChild(clone);
        });
      });

      if (this.expanded && this.selected.size === 0) this.closeSelectedPanel();
    }

    showInventoryNotice(variantId, persistent, message, failedQuantity, availableQuantity) {
      var id = String(variantId);
      var existingTimer = this.inventoryNoticeTimers.get(id);
      if (existingTimer) clearTimeout(existingTimer);
      this.inventoryNoticeTimers.delete(id);

      this.inventoryNotices.set(id, {
        message: message,
        persistent: Boolean(persistent),
        failedQuantity: toQuantity(failedQuantity),
        availableQuantity: Number.isFinite(availableQuantity) ? toQuantity(availableQuantity) : null
      });
      this.renderInventoryNotice(id);

      if (!persistent) {
        this.inventoryNoticeTimers.set(id, setTimeout(() => {
          var notice = this.inventoryNotices.get(id);
          if (notice && !notice.persistent) {
            this.inventoryNotices.delete(id);
            this.renderInventoryNotice(id);
          }
          this.inventoryNoticeTimers.delete(id);
        }, 2400));
      }
    }

    clearPersistentInventoryNotice(variantId) {
      var id = String(variantId);
      var notice = this.inventoryNotices.get(id);
      if (!notice || !notice.persistent) return;
      this.inventoryNotices.delete(id);
      this.renderInventoryNotice(id);
    }

    renderInventoryNotice(variantId) {
      var id = String(variantId);
      var notice = this.inventoryNotices.get(id);
      this.querySelectorAll('[data-variant-id="' + id + '"]').forEach((row) => {
        row.classList.toggle('is-inventory-error', Boolean(notice && notice.persistent));
        row.classList.toggle('is-inventory-warning', Boolean(notice && !notice.persistent));
        var message = row.querySelector('[data-bulk-inventory-message]');
        if (!message) return;
        message.textContent = notice ? notice.message : '';
        message.hidden = !notice;
      });
    }

    isInventoryCartError(message) {
      return /sold out|out of stock|not enough|unavailable|can't add more|cannot add more|inventory|stock/i.test(String(message || ''));
    }

    inventoryLookupError(message) {
      var error = new Error(message);
      error.isInventoryLookupError = true;
      return error;
    }

    storefrontApiVersion() {
      return String(this.dataset.storefrontApiVersion || '2026-07').trim() || '2026-07';
    }

    storefrontCountry() {
      return String(this.dataset.storefrontCountry || '').trim().toUpperCase();
    }

    storefrontEndpoint() {
      return window.location.origin + '/api/' + this.storefrontApiVersion() + '/graphql.json';
    }

    variantGid(variantId) {
      return 'gid://shopify/ProductVariant/' + String(variantId);
    }

    variantIdFromGid(gid) {
      return String(gid || '').split('/').pop();
    }

    marketInventoryMessage(availableQuantity) {
      var quantity = toQuantity(availableQuantity);
      return (this.dataset.onlyAvailableTemplate || '')
        .replace(/\[count\]/g, String(quantity));
    }

    async probeMarketInventory(items, cartQuantities) {
      var country = this.storefrontCountry();
      var availableById = new Map();

      if (!items.length) return availableById;
      if (!country) {
        throw this.inventoryLookupError('Missing Storefront country context.');
      }

      var query = [
        'mutation BulkInventoryProbe($input: CartInput!, $country: CountryCode!) @inContext(country: $country) {',
        '  cartCreate(input: $input) {',
        '    cart {',
        '      lines(first: 250) {',
        '        nodes {',
        '          id',
        '          quantity',
        '          merchandise {',
        '            ... on ProductVariant { id }',
        '          }',
        '        }',
        '      }',
        '    }',
        '    warnings { code target message }',
        '    userErrors { code field message }',
        '  }',
        '}'
      ].join('\n');

      for (var start = 0; start < items.length; start += 250) {
        var chunk = items.slice(start, start + 250);
        var inputLines = chunk.map((item) => {
          var id = String(item.id);
          var cartQuantity = cartQuantities.get(id) || 0;
          return {
            merchandiseId: this.variantGid(id),
            quantity: cartQuantity + toQuantity(item.quantity)
          };
        });

        try {
          var response = await fetch(this.storefrontEndpoint(), {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              query: query,
              variables: {
                country: country,
                input: {
                  buyerIdentity: {
                    countryCode: country
                  },
                  lines: inputLines
                }
              }
            })
          });
          var data = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw this.inventoryLookupError('Storefront cart inventory probe failed (' + response.status + ').');
          }
          if (Array.isArray(data.errors) && data.errors.length) {
            throw this.inventoryLookupError(data.errors.map((error) => error.message).join(' '));
          }
          var payload = data.data && data.data.cartCreate;
          if (!payload || !payload.cart || !payload.cart.lines || !Array.isArray(payload.cart.lines.nodes)) {
            throw this.inventoryLookupError('Storefront cart inventory response is incomplete.');
          }
          if (Array.isArray(payload.userErrors) && payload.userErrors.length) {
            throw this.inventoryLookupError(
              payload.userErrors.map((error) => error.message).join(' ')
            );
          }
          var acceptedById = new Map();
          payload.cart.lines.nodes.forEach((line) => {
            if (!line || !line.merchandise || !line.merchandise.id) return;
            var id = this.variantIdFromGid(line.merchandise.id);
            acceptedById.set(id, (acceptedById.get(id) || 0) + toQuantity(line.quantity));
          });

          chunk.forEach((item) => {
            var id = String(item.id);
            var cartQuantity = cartQuantities.get(id) || 0;
            var acceptedQuantity = acceptedById.get(id) || 0;
            availableById.set(id, Math.max(0, acceptedQuantity - cartQuantity));
          });
        } catch (error) {
          if (error && error.isInventoryLookupError) throw error;
          throw this.inventoryLookupError(error && error.message
            ? error.message
            : 'Storefront cart inventory probe failed.');
        }
      }

      return availableById;
    }

    inventoryConflicts(items, availableById) {
      var conflicts = [];

      items.forEach((item) => {
        var id = String(item.id);
        if (!availableById.has(id)) {
          throw this.inventoryLookupError('Missing market inventory for selected variant ' + id + '.');
        }
        var availableQuantity = availableById.get(id);
        if (toQuantity(item.quantity) > availableQuantity) {
          conflicts.push({
            id: id,
            availableQuantity: availableQuantity
          });
        }
      });

      return conflicts;
    }

    renderInventoryConflicts(items, conflicts) {
      var conflictIds = new Set(conflicts.map((conflict) => conflict.id));
      items.forEach((item) => {
        var id = String(item.id);
        if (!conflictIds.has(id)) this.clearPersistentInventoryNotice(id);
      });
      conflicts.forEach((conflict) => {
        this.showInventoryNotice(
          conflict.id,
          true,
          this.marketInventoryMessage(conflict.availableQuantity),
          this.getVariantQuantity(conflict.id),
          conflict.availableQuantity
        );
      });

      this.renderSelectedItems();
      conflicts.forEach((conflict) => {
        this.syncVariantInputs(conflict.id, this.getVariantQuantity(conflict.id));
      });
    }

    renderInventoryLookupFailure(items, error) {
      console.error('[Bulk purchase] Market inventory lookup failed:', error);
      items.forEach((item) => {
        this.showInventoryNotice(
          item.id,
          true,
          this.dataset.inventoryUnavailable || '',
          item.quantity,
          null
        );
      });
      this.renderSelectedItems();
      items.forEach((item) => {
        this.syncVariantInputs(String(item.id), this.getVariantQuantity(item.id));
      });
    }

    async validateMarketInventory(items, cartQuantities) {
      var quantities = cartQuantities;
      if (!quantities) {
        var freshCart = await this.fetchCart();
        if (!freshCart) {
          throw this.inventoryLookupError('Unable to refresh the cart before inventory validation.');
        }
        this.applyCart(freshCart);
        quantities = new Map(this.cartVariantQuantities);
      }

      var availableById = await this.probeMarketInventory(items, quantities);
      var conflicts = this.inventoryConflicts(items, availableById);
      this.renderInventoryConflicts(items, conflicts);

      return {
        cartQuantities: quantities,
        conflicts: conflicts
      };
    }

    async markCartInventoryError(items, message, cartQuantities) {
      if (!this.isInventoryCartError(message)) return false;

      try {
        var validation = await this.validateMarketInventory(items, cartQuantities);
        if (!validation.conflicts.length) {
          throw this.inventoryLookupError(
            'Shopify rejected the cart request, but no conflicting variant was returned by inventory validation.'
          );
        }
      } catch (error) {
        this.renderInventoryLookupFailure(items, error);
      }
      return true;
    }

    pendingCount() {
      var total = 0;
      this.selected.forEach((quantity) => {
        total += quantity;
      });
      return total;
    }

    updateState() {
      var pending = this.pendingCount();
      var message = this.evaluateMessage(this.cartCount, pending);
      var total = this.cartCount + pending;
      var max = this.maxTierCount || 1;
      var cartProgress = Math.min(this.cartCount, max);
      var pendingStart = Math.min(this.cartCount, max);
      var pendingEnd = Math.min(total, max);
      var pendingProgress = Math.max(0, pendingEnd - pendingStart);
      var showProgressMessage = this.dataset.showProgressMessage === 'true';

      if (this.progressMessageEl) {
        this.progressMessageEl.textContent = message;
        this.progressMessageEl.hidden = !showProgressMessage;
      }
      if (this.progressCartEl) this.progressCartEl.style.width = (cartProgress / max * 100).toFixed(2) + '%';
      if (this.progressPendingEl) {
        this.progressPendingEl.style.left = (pendingStart / max * 100).toFixed(2) + '%';
        this.progressPendingEl.style.width = (pendingProgress / max * 100).toFixed(2) + '%';
      }
      if (this.cartCountEl) this.cartCountEl.textContent = String(this.cartCount);
      if (this.pendingCountEl) this.pendingCountEl.textContent = String(pending);
      if (this.summaryCountEl) {
        this.summaryCountEl.textContent = (this.dataset.selectedTemplate || '[count]')
          .replace(/\[count\]/g, String(pending));
      }
      if (this.selectedCountEl) {
        this.selectedCountEl.textContent = pending + ' ' + pluralizeItems(
          pending,
          this.dataset.itemLabel,
          this.dataset.itemsLabel
        );
      }

      this.milestones.forEach((milestone, index) => {
        var count = toQuantity(milestone.dataset.count);
        var cartEarned = this.cartCount >= count;
        var pendingEarned = !cartEarned && total >= count;
        var earned = cartEarned || pendingEarned;
        var label = milestone.querySelector('[data-bulk-milestone-label]');
        milestone.style.left = Math.min(100, (count / max * 100)).toFixed(2) + '%';
        milestone.classList.toggle('is-cart-earned', cartEarned);
        milestone.classList.toggle('is-pending-earned', pendingEarned);
        milestone.classList.toggle('is-earned', earned);
        milestone.classList.toggle('is-first', index === 0);
        milestone.classList.toggle('is-last', index === this.milestones.length - 1);
        if (label) {
          label.textContent = String(toNumber(milestone.dataset.percent)) + '%';
        }
      });

      if (this.progressEl) {
        this.progressEl.setAttribute('aria-valuenow', String(Math.min(total, max)));
        this.progressEl.setAttribute('aria-valuemax', String(max));
        this.progressEl.setAttribute('aria-valuetext', message);
      }

      if (this.addButtonEl) this.addButtonEl.disabled = pending === 0 || this.loading;
      if (this.addButtonTextEl) {
        this.addButtonTextEl.textContent = pending > 0
          ? (this.dataset.addItemsTemplate || '').replace(/\[count\]/g, String(pending))
          : (this.dataset.addLabel || '');
      }

    }

    evaluateMessage(cartCount, pendingCount) {
      if (!this.tiers.length) return '';

      var total = cartCount + pendingCount;
      var current = null;
      var next = null;

      for (var i = 0; i < this.tiers.length; i++) {
        if (total >= this.tiers[i].count) {
          current = this.tiers[i];
        } else {
          next = this.tiers[i];
          break;
        }
      }

      if (!current) {
        var first = this.tiers[0];
        var needed = Math.max(0, first.count - total);
        return (this.dataset.unlockTemplate || '')
          .replace(/\[count\]/g, String(needed))
          .replace(/\[percent\]/g, String(first.percent));
      }

      if (pendingCount > 0 && (cartCount < current.count || !next)) {
        return replaceTokensWithFallback(
          this.dataset.readyLabel || '',
          this.dataset.readyFallback || '',
          { percent: current.percent, current_percent: current.percent }
        );
      }

      if (next) {
        var remaining = Math.max(0, next.count - total);
        return replaceTokensWithFallback(
          this.dataset.progressLabel || '',
          this.dataset.progressFallback || '',
          {
            current_percent: current.percent,
            next_percent: next.percent,
            remaining: remaining,
            percent: current.percent
          }
        );
      }

      return replaceTokensWithFallback(
        this.dataset.unlockedLabel || '',
        this.dataset.unlockedFallback || '',
        { percent: current.percent, current_percent: current.percent }
      );
    }

    toggleSelectedPanel() {
      if (this.expanded) {
        this.closeSelectedPanel();
      } else {
        this.openSelectedPanel();
      }
    }

    openSelectedPanel() {
      if (this.pendingCount() === 0) return;
      this.expanded = true;
      if (this.sheetPanelEl) this.sheetPanelEl.hidden = false;
      if (this.sheetBackdropEl) this.sheetBackdropEl.hidden = false;
      if (this.bottomBarEl) this.bottomBarEl.classList.add('is-expanded');
      this.classList.add('is-selected-expanded');
      this.updateBackToTop();
      this.querySelectorAll('[data-bulk-toggle-expanded]').forEach((button) => {
        button.setAttribute('aria-expanded', 'true');
      });
    }

    closeSelectedPanel() {
      this.expanded = false;
      if (this.sheetPanelEl) this.sheetPanelEl.hidden = true;
      if (this.sheetBackdropEl) this.sheetBackdropEl.hidden = true;
      if (this.bottomBarEl) this.bottomBarEl.classList.remove('is-expanded');
      this.classList.remove('is-selected-expanded');
      this.updateBackToTop();
      this.querySelectorAll('[data-bulk-toggle-expanded]').forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });
    }

    setError(message, type) {
      this.errorType = message ? (type || '') : '';
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    scheduleCartSync() {
      if (this._cartSyncTimer) clearTimeout(this._cartSyncTimer);
      this._cartSyncTimer = setTimeout(() => this.syncCartCount(), 220);
    }

    async syncCartCount() {
      try {
        var cart = await this.fetchCart();
        if (cart) {
          this.applyCart(cart);
        }
      } catch (_) {
        /* Cart progress remains on the last known state. */
      }
    }

    async fetchCart() {
      var cartUrl = withJsonSuffix(this.dataset.cartUrl || (window.theme && theme.routes && theme.routes.cart) || '/cart');
      if (!cartUrl) return null;
      var response = await fetch(cartUrl, {
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return null;
      return response.json();
    }

    applyCart(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      var count = 0;
      var cartVariantQuantities = new Map();
      cart.items.forEach((item) => {
        var variantId = String(item.variant_id || item.id || '');
        if (variantId) {
          cartVariantQuantities.set(
            variantId,
            (cartVariantQuantities.get(variantId) || 0) + toQuantity(item.quantity)
          );
        }
        if (this.eligibleProductIds.has(String(item.product_id))) {
          count += toQuantity(item.quantity);
        }
      });
      this.cartCount = count;
      this.cartVariantQuantities = cartVariantQuantities;
      this.dataset.cartCount = String(count);
      this.updateState();
    }

    cartAddUrl() {
      return withJsonSuffix(
        this.dataset.cartAddUrl ||
        (window.theme && theme.routes && theme.routes.cartAdd) ||
        '/cart/add'
      );
    }

    sectionsToRender() {
      var sections = ['cart-icon-bubble'];
      var cartDrawer = document.querySelector('cart-drawer');
      var drawerSection = cartDrawer && cartDrawer.closest('.shopify-section');
      if (drawerSection && drawerSection.id) {
        sections.push(drawerSection.id.replace('shopify-section-', ''));
      }
      return unique(sections);
    }

    refreshAndOpenCartDrawer() {
      var cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;

      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      if (this._cartDrawerTimer) clearTimeout(this._cartDrawerTimer);
      this._cartDrawerTimer = setTimeout(() => {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
          bubbles: true,
          detail: { opener: this.addButtonEl }
        }));
        this._cartDrawerTimer = null;
      }, 700);
    }

    async addToCart() {
      var pending = this.pendingCount();
      if (this.loading || pending === 0) return;

      var items = this.resultRows.map((row) => {
        var quantity = this.selected.get(String(row.dataset.variantId));
        if (!quantity) return null;
        return {
          id: Number(row.dataset.variantId),
          quantity: quantity
        };
      }).filter(Boolean);

      if (!items.length) return;

      this.loading = true;
      this.classList.add('is-loading');
      this.setError('');
      this.updateState();

      var cartQuantitiesBeforeAdd = null;

      try {
        var validation = await this.validateMarketInventory(items);
        cartQuantitiesBeforeAdd = validation.cartQuantities;
        if (validation.conflicts.length) {
          this.setError('');
          this.openSelectedPanel();
          return;
        }

        var response = await fetch(this.cartAddUrl(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: items,
            sections: this.sectionsToRender(),
            sections_url: window.location.pathname
          })
        });
        var data = await response.json().catch(() => ({}));

        if (!response.ok || data.status) {
          throw new Error(this.cartErrorMessage(data, response.status));
        }

        this.renderReturnedSections(data.sections);
        var freshCart = await this.fetchCart();
        if (freshCart) this.applyCart(freshCart);

        this.clearSelection();
        this.closeSelectedPanel();

        document.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: {
            cart: freshCart || null,
            variantId: items[0] ? items[0].id : null
          }
        }));
        this.refreshAndOpenCartDrawer();
      } catch (error) {
        if (error && error.isInventoryLookupError) {
          this.renderInventoryLookupFailure(items, error);
          this.setError('');
          this.openSelectedPanel();
        } else if (await this.markCartInventoryError(items, error.message, cartQuantitiesBeforeAdd)) {
          this.setError('');
          this.openSelectedPanel();
        } else {
          this.setError(error.message || (window.theme && theme.strings && theme.strings.cartError) || '');
        }
      } finally {
        this.loading = false;
        this.classList.remove('is-loading');
        this.updateState();
      }
    }

    renderReturnedSections(sections) {
      if (!sections || typeof sections !== 'object') return;
      var cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && sections['cart-icon-bubble']) {
        cartIconBubble.innerHTML = sections['cart-icon-bubble'];
      }
    }

    cartErrorMessage(data, status) {
      if (data) {
        if (typeof data.description === 'string') return data.description;
        if (typeof data.message === 'string') return data.message;
        if (data.errors && typeof data.errors === 'object') {
          return Object.keys(data.errors).map((key) => {
            var value = data.errors[key];
            return Array.isArray(value) ? value.join(', ') : value;
          }).join(', ');
        }
      }
      return (window.theme && theme.strings && theme.strings.cartError) || '';
    }
  }

  if (!customElements.get('bulk-purchase')) {
    customElements.define('bulk-purchase', BulkPurchase);
  }
})();
